import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Tables } from '../lib/supabase/types';
import type { ColorTheme, ThemeMode } from '../lib/themes/theme-config';

type Country = Tables<'countries'>;
type State = Tables<'states'>;
type City = Tables<'cities'>;

export type Language = 'en' | 'es' | 'ar' | 'fa' | 'ku' | 'ckb' | 'ro';

interface AppState {
  // Location data
  countries: Country[];
  states: State[];
  cities: City[];

  // Theme settings
  colorTheme: ColorTheme;
  mode: ThemeMode;

  // Language settings
  language: Language;

  // Device info
  deviceFingerprint: string | null;

  // Actions
  setCountries: (countries: Country[]) => void;
  setStates: (states: State[]) => void;
  setCities: (cities: City[]) => void;
  setColorTheme: (colorTheme: ColorTheme) => void;
  setMode: (mode: ThemeMode) => void;
  setLanguage: (language: Language) => void;
  setDeviceFingerprint: (fingerprint: string) => void;
  reset: () => void;
}

const initialState = {
  countries: [],
  states: [],
  cities: [],
  colorTheme: 'ocean' as ColorTheme,
  mode: 'system' as ThemeMode,
  language: 'en' as Language,
  deviceFingerprint: null,
};

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      ...initialState,

      setCountries: (countries) => set({ countries }),
      setStates: (states) => set({ states }),
      setCities: (cities) => set({ cities }),
      setColorTheme: (colorTheme) => set({ colorTheme }),
      setMode: (mode) => set({ mode }),
      setLanguage: (language) => set({ language }),
      setDeviceFingerprint: (deviceFingerprint) => set({ deviceFingerprint }),
      reset: () => set(initialState),
    }),
    {
      name: 'candle-app-storage',
    }
  )
);
